/*
 * This code is protected by intellectual property rights.
 * Dr. Ing. h.c. F. Porsche AG owns exclusive rights of use.
 * © 2017-2026, Dr. Ing. h.c. F. Porsche AG.
 */

import { AfterContentInit, Component, ElementRef, inject, OnInit, ChangeDetectionStrategy } from '@angular/core'
import { ConfigService } from '../services/config/config.service'
import { ActivatedRoute } from '@angular/router'
import { UntilDestroy } from '@ngneat/until-destroy'
import { PidxGoogleTagManagerIntegrationService } from '../services/google-tag-manager/pidx-google-tag-manager-integration.service'
import { GTMConfig } from '../services/google-tag-manager/pidx-google-tag-manager-integration.interfaces'
import { FullstoryService } from '../services/fullstory/fullstory.service'

import { Subject, takeUntil } from 'rxjs'

export enum Layout {
  DEFAULT_LAYOUT = 'DEFAULT_LAYOUT',
  PROSPECT_LAYOUT = 'PROSPECT_LAYOUT',
  SUCCESS_LAYOUT = 'SUCCESS_LAYOUT',
  MAINTENANCE_LAYOUT = 'MAINTENANCE_LAYOUT',
}

@UntilDestroy()
@Component({
  selector: 'myprofile-signup-ui-layout',
  templateUrl: './layout.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class LayoutComponent implements OnInit, AfterContentInit {
  private _elementRef = inject(ElementRef)
  private _configService = inject(ConfigService)
  private _googleTagManagerIntegration = inject(PidxGoogleTagManagerIntegrationService)
  private _fullstoryService = inject(FullstoryService)
  private _activatedRoute = inject(ActivatedRoute)

  public layout = Layout.DEFAULT_LAYOUT
  public isProd = false
  public locale = 'en-GB'
  public navMode = 'nav'
  public navTheme = 'light'
  public navLimitedWidth = true

  private readonly _locale: string
  private readonly _rtlLanguages = ['ar_AE']
  private _unsubscribe$ = new Subject<void>()

  /** SUCCESS/MAINTENANCE: footer below viewport from sm (760px). */
  public get isCompactMinHeightLayout(): boolean {
    return this.layout === Layout.SUCCESS_LAYOUT || this.layout === Layout.MAINTENANCE_LAYOUT
  }

  /** PROSPECT/register: footer below viewport from lg (1300px). DEFAULT keeps a sticky footer in-view. */
  public get isProspectMinHeightLayout(): boolean {
    return this.layout === Layout.PROSPECT_LAYOUT
  }

  constructor() {
    this._locale = this._configService.getUrlLocale()
    this._activatedRoute.data.pipe(takeUntil(this._unsubscribe$)).subscribe(async (data) => {
      this.layout = data['layout']
    })
  }

  ngOnInit() {
    this.setDirection()
  }

  private setDirection(): void {
    const direction = this._rtlLanguages.includes(this._locale) ? 'rtl' : 'ltr'
    this._elementRef.nativeElement.parentElement.setAttribute('dir', direction)
  }

  public ngAfterContentInit() {
    if (!this._configService.isCNDomain()) {
      this._setupGATracking()
      this._fullstoryService.setupFullstory()
    }

    if (Layout.SUCCESS_LAYOUT == this.layout || Layout.MAINTENANCE_LAYOUT == this.layout) {
      this.navMode = 'hero'
      this.navTheme = 'dark'
      this.navLimitedWidth = false
    }
  }

  private async _setupGATracking() {
    const trackingConfig: GTMConfig = {
      country: this._configService.getUrlCountry(),
      language: this._configService.getUrlLocale(),
      environment: this._configService.getGTMEnvironment(),
      applicationId: this._configService.getGTMApplicationID(),
      server: new URL(this._configService.getBaseUrl()).hostname,
    }
    await this._googleTagManagerIntegration.init(trackingConfig, this._configService.getGTMIsEnabled())
  }

  protected readonly Layout = Layout
}
